/**
 * Notifications inbox — TanStack infinite query over the user's
 * historical notifications + mark-read mutation.
 */
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../supabase'
import { useAuth } from './use-auth'

const PAGE_SIZE = 20

export interface InboxNotification {
  id: string
  profile_id: string
  title: string
  body: string
  data: Record<string, unknown> | null
  channel: 'push' | 'email' | 'sms' | 'in_app'
  is_read: boolean
  read_at: string | null
  sent_at: string | null
  created_at: string
}

export const notificationKeys = {
  all: ['notifications'] as const,
  inbox: (userId: string) => [...notificationKeys.all, 'inbox', userId] as const,
  unread: (userId: string) => [...notificationKeys.all, 'unread', userId] as const,
}

export function useNotificationsInbox() {
  const { user } = useAuth()
  const userId = user?.id ?? ''

  return useInfiniteQuery({
    queryKey: notificationKeys.inbox(userId),
    queryFn: async ({ pageParam }: { pageParam: string | null }) => {
      let query = supabase
        .from('notifications')
        .select('*')
        .eq('profile_id', userId)
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE)

      if (pageParam) query = query.lt('created_at', pageParam)

      const { data, error } = await query
      if (error) throw error
      return (data ?? []) as InboxNotification[]
    },
    enabled: !!userId,
    initialPageParam: null as string | null,
    getNextPageParam: (last) =>
      last.length === PAGE_SIZE ? last[last.length - 1]!.created_at : undefined,
    staleTime: 15 * 1000,
  })
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('id', notificationId)
      if (error) throw error
    },
    // Optimistic flip — the read indicator (badge dot, dim styling) updates
    // before the network call completes, which matches the speed users get
    // on Notion / Linear / Apple Mail. We patch every page of the infinite
    // query that contains the target id and decrement the unread counter.
    // Rollback on error restores the prior pages snapshot.
    onMutate: async (notificationId) => {
      if (!user?.id) return undefined
      const inboxKey = notificationKeys.inbox(user.id)
      const unreadKey = notificationKeys.unread(user.id)
      await queryClient.cancelQueries({ queryKey: inboxKey })
      await queryClient.cancelQueries({ queryKey: unreadKey })

      const previousInbox = queryClient.getQueryData(inboxKey)
      const previousUnread = queryClient.getQueryData(unreadKey)
      const readAt = new Date().toISOString()

      queryClient.setQueryData(inboxKey, (old: unknown) => {
        if (!old || typeof old !== 'object' || !('pages' in old)) return old
        const data = old as { pages: InboxNotification[][] } & Record<string, unknown>
        return {
          ...data,
          pages: data.pages.map((page) =>
            page.map((n) =>
              n.id === notificationId && !n.is_read ? { ...n, is_read: true, read_at: readAt } : n,
            ),
          ),
        }
      })
      queryClient.setQueryData(unreadKey, (old: unknown) => {
        if (typeof old !== 'number') return old
        return Math.max(0, old - 1)
      })
      return { previousInbox, previousUnread, inboxKey, unreadKey }
    },
    onError: (_err, _id, context) => {
      if (!context) return
      if (context.previousInbox !== undefined) {
        queryClient.setQueryData(context.inboxKey, context.previousInbox)
      }
      if (context.previousUnread !== undefined) {
        queryClient.setQueryData(context.unreadKey, context.previousUnread)
      }
    },
    onSettled: () => {
      if (!user?.id) return
      queryClient.invalidateQueries({ queryKey: notificationKeys.inbox(user.id) })
      queryClient.invalidateQueries({ queryKey: notificationKeys.unread(user.id) })
    },
  })
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async () => {
      if (!user?.id) return
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('profile_id', user.id)
        .eq('is_read', false)
      if (error) throw error
    },
    onSuccess: () => {
      if (!user?.id) return
      queryClient.invalidateQueries({ queryKey: notificationKeys.inbox(user.id) })
      queryClient.invalidateQueries({ queryKey: notificationKeys.unread(user.id) })
    },
  })
}
